import { parseMultiselectValue, visibleFlowOptions } from "@repo/shared";
import type { FlowField } from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api/_client";
import { useFieldValues, useSaveFieldValue } from "@/lib/hooks/use-flow-instances";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./wizard-field-renderer.i18n";

type WizardFieldRendererProps = {
  field: FlowField;
  instanceId: string;
  isCompleted: boolean;
  goalId?: string;
  portfolioId?: string;
  hasValidationError?: boolean;
};

export function WizardFieldRenderer({
  field,
  instanceId,
  isCompleted,
  goalId,
  portfolioId,
  hasValidationError = false,
}: WizardFieldRendererProps) {
  const t = useT(T);
  const { fieldValues } = useFieldValues(instanceId);
  const saveField = useSaveFieldValue(
    instanceId,
    `field-value:${instanceId}:${field.id}:${goalId ?? ""}:${portfolioId ?? ""}`,
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDisabled = isCompleted;

  // Resolve server value from the field-values query (3-way scope: global / goal / portfolio)
  const serverValue = (() => {
    if (!fieldValues?.values) return "";
    if (field.scope === "portfolio" && portfolioId) {
      const match = fieldValues.values.find(
        (v) => v.flowFieldId === field.id && v.portfolioId === portfolioId,
      );
      return match?.value || "";
    }
    if (field.scope === "goal" && goalId) {
      const match = fieldValues.values.find(
        (v) => v.flowFieldId === field.id && v.goalId === goalId,
      );
      return match?.value || "";
    }
    const match = fieldValues.values.find(
      (v) => v.flowFieldId === field.id && !v.goalId && !v.portfolioId,
    );
    return match?.value || "";
  })();

  // Local state for instant UI, initialized from server value
  const [localValue, setLocalValue] = useState(serverValue);
  // Tracks whether the user has edits the server hasn't echoed back yet.
  const dirtyRef = useRef(false);

  // Adopt the server value only when the field is "clean" — i.e. the user has
  // no in-flight edits and no save is pending. Otherwise the debounced save
  // echoing back through setQueryData (or invalidateCompletion re-rendering the
  // tree) would overwrite characters the user is still typing, which manifested
  // as inputs "freezing" after a page refresh. Once the server value catches up
  // to what we have locally, the field is clean again and future external
  // updates (e.g. another component saving) are adopted.
  useEffect(() => {
    if (serverValue === localValue) {
      dirtyRef.current = false;
      return;
    }
    if (dirtyRef.current || saveField.isPending) return;
    setLocalValue(serverValue);
  }, [serverValue, localValue, saveField.isPending]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const doSave = (value: string) => {
    if (!isCompleted) {
      // Only pass the scope-relevant ID — global fields must not carry goalId/portfolioId,
      // otherwise the service stores a scoped row instead of updating the global one.
      saveField.mutate(
        {
          fieldId: field.id,
          value,
          goalId: field.scope === "goal" ? goalId : undefined,
          portfolioId: field.scope === "portfolio" ? portfolioId : undefined,
        },
        {
          // QPBI-395 fix #2 — the server can now reject a value (400). Surface
          // it and drop the local edit so the control resyncs to server state.
          onError: (err) => {
            toast.error(err instanceof ApiError ? err.message : t.saveFailed);
            dirtyRef.current = false;
            setLocalValue(serverValue);
          },
        },
      );
    }
  };

  // Debounced auto-save: fires 1 second after last keystroke
  const scheduleSave = (value: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSave(value), 1000);
  };

  const handleChange = (value: string) => {
    dirtyRef.current = true;
    setLocalValue(value);
    scheduleSave(value);
  };

  // Flush on blur: immediately fire any pending debounced save
  const handleBlur = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      doSave(localValue);
    }
  };

  // Dropdowns: save immediately on change (single discrete action)
  const handleDropdownChange = (val: string) => {
    dirtyRef.current = true;
    setLocalValue(val);
    doSave(val);
  };

  // Field key for React input IDs
  const fieldKey =
    field.scope === "goal" && goalId ? `__field_goal_${goalId}_${field.id}` : field.id;

  const errorClass = hasValidationError ? "border-destructive" : "";
  const wrapWithError = (input: React.ReactNode) =>
    hasValidationError ? (
      <div className="space-y-1">
        {input}
        <p className="text-xs text-destructive">{t.fieldRequired}</p>
      </div>
    ) : (
      input
    );

  switch (field.type) {
    case "text":
    case "email":
    case "phone":
      return wrapWithError(
        <Input
          id={fieldKey}
          type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={isDisabled}
          className={errorClass}
        />,
      );

    case "number": {
      // QPBI-395 — a stored value the native number input cannot represent
      // (decimal comma "1,5", "abc") would render blank while conditional
      // rules still read it; fall back to a text input so the advisor sees
      // what is actually saved.
      const numberUnrepresentable = localValue !== "" && Number.isNaN(Number(localValue));
      return wrapWithError(
        <Input
          id={fieldKey}
          type={numberUnrepresentable ? "text" : "number"}
          inputMode="decimal"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={isDisabled}
          className={errorClass}
        />,
      );
    }

    case "textarea":
      return wrapWithError(
        <Textarea
          id={fieldKey}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={isDisabled}
          rows={4}
          className={errorClass}
        />,
      );

    case "date":
      return wrapWithError(
        // QL-108 — DatePicker shows the tenant date format; the stored/sent
        // value stays yyyy-MM-dd. Complete dates save immediately (discrete
        // action, like dropdowns).
        <DatePicker
          id={fieldKey}
          value={localValue}
          onChange={handleDropdownChange}
          required={field.isRequired}
          disabled={isDisabled}
          className={errorClass}
        />,
      );

    case "datetime":
      return wrapWithError(
        // QL-211 — shows the viewer's local time and saves UTC
        // YYYY-MM-DDTHH:mm:ssZ once both date and time are complete. The
        // debounced path, not an immediate save: the native time input
        // reports every segment edit, so typing 10:30 would save 01:30 first.
        <DateTimePicker
          id={fieldKey}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={isDisabled}
          className={errorClass}
        />,
      );

    case "dropdown": {
      const options = visibleFlowOptions(field.options, [localValue]);
      return wrapWithError(
        <Select value={localValue} onValueChange={handleDropdownChange} disabled={isDisabled}>
          <SelectTrigger id={fieldKey} className={errorClass}>
            <SelectValue placeholder={t.selectAnOption} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      );
    }

    case "radio": {
      const options = visibleFlowOptions(field.options, [localValue]);
      return wrapWithError(
        <RadioGroup
          value={localValue}
          onValueChange={handleDropdownChange}
          disabled={isDisabled}
          className={hasValidationError ? "rounded-md border border-destructive p-2" : undefined}
        >
          {options.map((option) => (
            <OptionRow key={option.id}>
              <RadioGroupItem id={`${fieldKey}_${option.id}`} value={option.id} />
              <Label htmlFor={`${fieldKey}_${option.id}`} className="font-normal">
                {option.label}
              </Label>
            </OptionRow>
          ))}
        </RadioGroup>,
      );
    }

    case "boolean":
      // Stores "true"/"false" explicitly on toggle so conditions can match
      // either answer. A required boolean must be ticked ("true") to count as
      // filled — consent-style, like an HTML required checkbox. The field name
      // is rendered as a label by the page renderer.
      return wrapWithError(
        <OptionRow
          className={hasValidationError ? "rounded-md border border-destructive p-2" : undefined}
        >
          <Checkbox
            id={fieldKey}
            checked={localValue === "true"}
            onCheckedChange={(checked) => handleDropdownChange(checked === true ? "true" : "false")}
            disabled={isDisabled}
          />
        </OptionRow>,
      );

    case "multiselect": {
      const selected = parseMultiselectValue(localValue);
      const options = visibleFlowOptions(field.options, selected);
      const toggleOption = (optionId: string, checked: boolean) => {
        const next = checked ? [...selected, optionId] : selected.filter((id) => id !== optionId);
        // Keep stored order stable by option order, and store "" when nothing
        // is selected so required-validation treats it as empty.
        const ordered = options.filter((o) => next.includes(o.id)).map((o) => o.id);
        handleDropdownChange(ordered.length > 0 ? JSON.stringify(ordered) : "");
      };
      return wrapWithError(
        <div
          className={hasValidationError ? "rounded-md border border-destructive p-2" : undefined}
        >
          {options.map((option) => (
            <OptionRow key={option.id}>
              <Checkbox
                id={`${fieldKey}_${option.id}`}
                checked={selected.includes(option.id)}
                onCheckedChange={(checked) => toggleOption(option.id, checked === true)}
                disabled={isDisabled}
              />
              <Label htmlFor={`${fieldKey}_${option.id}`} className="font-normal">
                {option.label}
              </Label>
            </OptionRow>
          ))}
        </div>,
      );
    }

    default:
      return null;
  }
}
